import React from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '../../../components/app';
import { cn } from '../../../lib/utils';
import { FounderTransactionKind } from '../../../api/treasury';
import { TransactionDialogProps } from '../types';
import { KIND_LABELS } from './TransactionsTable';

const KIND_HINTS: Record<FounderTransactionKind, string> = {
  contribution: 'Основатель положил деньги на Счёт компании: Касса и Долг перед ним растут.',
  reimbursement: 'Компания вернула вложенное: Касса и Долг падают.',
  profit: 'Основатель забрал заработанное: падает только Касса.',
};

export function TransactionDialog({
  isOpen,
  onOpenChange,
  founders,
  formData,
  onFormDataChange,
  onSubmit,
  isEditing,
  isSubmitting,
  debtByFounder,
  profitAvailableByFounder,
  formatMoney,
}: TransactionDialogProps) {
  // Перебор не запрещаем — реальность бывает грязной, — но показываем явно.
  const limit =
    formData.kind === 'reimbursement'
      ? debtByFounder[formData.payerId]
      : formData.kind === 'profit'
        ? profitAvailableByFounder[formData.payerId]
        : undefined;
  const isOverLimit = limit !== undefined && formData.amount > limit;

  const limitHint =
    limit !== undefined ? (
      <span className={cn('inline-flex items-center gap-1', isOverLimit && 'font-semibold text-warn')}>
        {isOverLimit && <AlertTriangle className="size-3" aria-hidden />}
        {formData.kind === 'reimbursement' ? 'Долг перед ним' : 'Доступно взять'}: {formatMoney(limit)}
        {isOverLimit && ' — берёте больше, чем есть'}
      </span>
    ) : undefined;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl p-0">
        <DialogHeader bordered>
          <DialogTitle>{isEditing ? 'Изменить движение' : 'Добавить движение'}</DialogTitle>
          <DialogDescription>{KIND_HINTS[formData.kind]}</DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Дата" required>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => onFormDataChange((prev) => ({ ...prev, date: e.target.value }))}
              />
            </FormField>
            <FormField label="Основатель" required>
              {(p) => (
                <Select
                  value={formData.payerId}
                  onValueChange={(value) => onFormDataChange((prev) => ({ ...prev, payerId: value }))}
                >
                  <SelectTrigger {...p}>
                    <SelectValue placeholder="Выберите основателя" />
                  </SelectTrigger>
                  <SelectContent>
                    {founders.map((founder) => (
                      <SelectItem key={founder.id} value={founder.id}>
                        {founder.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormField>
          </div>

          <FormField label="Вид движения" required>
            {(p) => (
              <Select
                value={formData.kind}
                onValueChange={(value) => onFormDataChange((prev) => ({ ...prev, kind: value as FounderTransactionKind }))}
              >
                <SelectTrigger {...p}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(KIND_LABELS) as FounderTransactionKind[]).map((kind) => (
                    <SelectItem key={kind} value={kind}>
                      {KIND_LABELS[kind]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </FormField>

          <FormField label="Сумма, сум" required hint={limitHint}>
            <Input
              type="number"
              step="0.01"
              min="0"
              inputMode="decimal"
              value={formData.amount || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
            />
          </FormField>

          <FormField label="Комментарий">
            <Input
              type="text"
              placeholder="Например: перевод на карту 18 августа"
              value={formData.comment || ''}
              onChange={(e) => onFormDataChange((prev) => ({ ...prev, comment: e.target.value }))}
            />
          </FormField>
        </DialogBody>

        <DialogFooter bordered>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Отмена
          </Button>
          <Button onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            {isEditing ? 'Сохранить' : 'Добавить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
